import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ConfirmationService, MessageService } from 'primeng/api';
import { lastValueFrom } from 'rxjs';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import {
  SessionKillerConfig,
  SessionKillerLog,
  SessionKillerService,
  SessionKillerTarget,
  SessionKillerWhitelist
} from 'src/app/services/session-killer.service';

@Component({
  selector: 'app-session-killer',
  templateUrl: './session-killer.component.html',
  styleUrls: ['./session-killer.component.scss'],
  providers: [MessageService, ConfirmationService]
})
export class SessionKillerComponent implements OnInit {
  private title = inject(Title);
  private sessionKillerService = inject(SessionKillerService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);

  config: SessionKillerConfig | null = null;
  targets: SessionKillerTarget[] = [];
  logs: SessionKillerLog[] = [];
  whitelist: SessionKillerWhitelist[] = [];
  savingConfig = false;
  filterTypeOptions = [
    { label: 'LOGIN', value: 'LOGIN' },
    { label: 'PROGRAM_NAME', value: 'PROGRAM_NAME' },
    { label: 'HOST_NAME', value: 'HOST_NAME' }
  ];

  configForm = new FormGroup({
    maxMinutes: new FormControl<number>(5, {
      nonNullable: true,
      validators: [Validators.required, Validators.min(1), Validators.max(1440)]
    })
  });

  whitelistForm = new FormGroup({
    filterType: new FormControl<string>('LOGIN', {
      nonNullable: true,
      validators: [Validators.required]
    }),
    filterValue: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(256)]
    }),
    notes: new FormControl<string>('', { nonNullable: true })
  });

  async ngOnInit(): Promise<void> {
    this.title.setTitle('Session Killer');
    await this.reloadAll();
  }

  async reloadAll(): Promise<void> {
    LoaderService.showLoader();
    try {
      await Promise.all([
        this.fetchConfig(),
        this.fetchWhitelist(),
        this.fetchLogs()
      ]);
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async loadLogs(): Promise<void> {
    LoaderService.showLoader();
    try {
      await this.fetchLogs();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  private async fetchConfig(): Promise<void> {
    this.config = await lastValueFrom(this.sessionKillerService.getConfig());
    this.configForm.controls.maxMinutes.setValue(this.config.maxMinutes ?? 5);
  }

  private async fetchWhitelist(): Promise<void> {
    this.whitelist = await lastValueFrom(
      this.sessionKillerService.listWhitelist()
    );
  }

  private async fetchLogs(): Promise<void> {
    this.logs = await lastValueFrom(this.sessionKillerService.listLogs());
  }

  async onToggleEnabled(enabled: boolean): Promise<void> {
    if (!this.config) {
      return;
    }
    const previous = this.config.isEnabled;
    this.config.isEnabled = enabled;
    this.savingConfig = true;
    try {
      this.config = await lastValueFrom(
        this.sessionKillerService.updateConfig({ isEnabled: enabled })
      );
      this.messageService.add({
        severity: enabled ? 'warn' : 'success',
        summary: enabled ? 'Trava ligada' : 'Trava desligada',
        detail: enabled
          ? 'KILL automático ativo. Sessões longas serão encerradas.'
          : 'Procedure continua instalada, mas não mata sessões.'
      });
    } catch (error) {
      this.config.isEnabled = previous;
      AppDialogService.showErrorDialog(error);
    } finally {
      this.savingConfig = false;
    }
  }

  async onToggleLogging(enabled: boolean): Promise<void> {
    if (!this.config) {
      return;
    }
    const previous = this.config.isLoggingEnabled;
    this.config.isLoggingEnabled = enabled;
    this.savingConfig = true;
    try {
      this.config = await lastValueFrom(
        this.sessionKillerService.updateConfig({ isLoggingEnabled: enabled })
      );
      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: enabled ? 'Log habilitado' : 'Log desabilitado'
      });
    } catch (error) {
      this.config.isLoggingEnabled = previous;
      AppDialogService.showErrorDialog(error);
    } finally {
      this.savingConfig = false;
    }
  }

  async saveMaxMinutes(): Promise<void> {
    if (this.configForm.invalid) {
      this.configForm.markAllAsTouched();
      return;
    }
    const maxMinutes = this.configForm.controls.maxMinutes.value;
    this.savingConfig = true;
    LoaderService.showLoader();
    try {
      this.config = await lastValueFrom(
        this.sessionKillerService.updateConfig({ maxMinutes })
      );
      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: `MaxMinutes atualizado para ${maxMinutes}`
      });
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      this.savingConfig = false;
      LoaderService.showLoader(false);
    }
  }

  async runDryRun(): Promise<void> {
    LoaderService.showLoader();
    try {
      this.targets = await lastValueFrom(this.sessionKillerService.dryRun());
      this.messageService.add({
        severity: 'info',
        summary: 'Dry-run',
        detail: `${this.targets.length} sessão(ões) candidata(s) — nenhum KILL executado`
      });
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async addWhitelist(): Promise<void> {
    if (this.whitelistForm.invalid) {
      this.whitelistForm.markAllAsTouched();
      return;
    }
    LoaderService.showLoader();
    try {
      const value = this.whitelistForm.getRawValue();
      await lastValueFrom(
        this.sessionKillerService.addWhitelist({
          filterType: value.filterType,
          filterValue: value.filterValue,
          notes: value.notes || null,
          isEnabled: true
        })
      );
      this.whitelistForm.reset({
        filterType: 'LOGIN',
        filterValue: '',
        notes: ''
      });
      await this.fetchWhitelist();
      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: 'Whitelist adicionada'
      });
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async toggleWhitelist(
    entry: SessionKillerWhitelist,
    enabled: boolean
  ): Promise<void> {
    if (!entry.whitelistId) {
      return;
    }
    const previous = entry.isEnabled;
    entry.isEnabled = enabled;
    try {
      await lastValueFrom(
        this.sessionKillerService.updateWhitelist(entry.whitelistId, {
          isEnabled: enabled
        })
      );
    } catch (error) {
      entry.isEnabled = previous;
      AppDialogService.showErrorDialog(error);
    }
  }

  confirmDeleteWhitelist(entry: SessionKillerWhitelist): void {
    this.confirmationService.confirm({
      acceptLabel: 'Sim',
      rejectLabel: 'Não',
      rejectButtonStyleClass: 'p-button-danger',
      header: 'Remover whitelist',
      message: `Remover ${entry.filterType} = ${entry.filterValue}?`,
      accept: async () => {
        if (!entry.whitelistId) {
          return;
        }
        LoaderService.showLoader();
        try {
          await lastValueFrom(
            this.sessionKillerService.deleteWhitelist(entry.whitelistId)
          );
          await this.fetchWhitelist();
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Whitelist removida'
          });
        } catch (error) {
          AppDialogService.showErrorDialog(error);
        } finally {
          LoaderService.showLoader(false);
        }
      }
    });
  }
}
